// ═══════════════════════════════════════════════════════════════════════════
// planPeriod — "FY27 (Jul 2026 – Jun 2027)" from a plan's start and end dates.
//
// ⚠️ BYTE-IDENTICAL IN BOTH REPOS, like `WigLinkChip`. `diff` is the test:
//
//   10x-frontend/.../UserPanel/AnnualOkr/_shared/planPeriod.js
//   10x-coach-frontend/.../CoachPanel/AnnualOkr/_shared/planPeriod.js
//
// Used wherever an Annual OKR period is shown: the scorecard header (both
// apps), and in the CEO app the landing's period picker, its fixed-period line
// and the line under the CTA once a run exists.
//
// ⚠️ THE FY LABEL IS BUILT HERE, FROM `end_date` (AO-443, agreed with BE
// 2026-10-16). "FY27" means the financial year that ENDS in 2027, so a plan
// running Jul 2026 – Jun 2027 is FY27. The server's `cycle_label` is not used
// for this: it was "FY28" for that same plan, and for APP Program companies it
// is the programme's name ("APP"), which says nothing about the period.
//
// ⚠️ PARSED BY SPLITTING THE STRING, NOT `new Date(value)`. A date-only ISO
// string is parsed as UTC midnight and rendered in the viewer's zone, so
// anyone west of Greenwich would see a plan starting 1 Jul as starting 30 Jun —
// and, at the year boundary, the wrong FY. These are calendar dates with no
// time in them.
// ═══════════════════════════════════════════════════════════════════════════

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// "2026-07-01" (or "2026-07-01T00:00:00.000Z") → { year: 2026, month: "Jul" }.
// Null for anything that isn't a usable date.
const parseDate = (value) => {
  const [year, month] = String(value || "")
    .slice(0, 10)
    .split("-");
  const monthName = MONTHS[Number(month) - 1];
  if (!/^\d{4}$/.test(year || "") || !monthName) return null;
  return { year: Number(year), month: monthName };
};

// "FY27" from the plan's end date. Null when there is no usable end date.
export const fyLabelFor = (endDate) => {
  const end = parseDate(endDate);
  return end ? `FY${String(end.year).slice(-2)}` : null;
};

// "FY27 (Jul 2026 – Jun 2027)".
//
// Partial data degrades rather than inventing a span: an end date alone gives
// "FY27 (ends Jun 2027)", a start date alone gives "From Jul 2026", and
// neither gives null — so the caller drops the line instead of printing a
// label from somewhere else.
export const formatPlanPeriod = (startDate, endDate) => {
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  const fy = fyLabelFor(endDate);

  if (start && end) {
    return `${fy} (${start.month} ${start.year} – ${end.month} ${end.year})`;
  }
  if (end) return `${fy} (ends ${end.month} ${end.year})`;
  if (start) return `From ${start.month} ${start.year}`;
  return null;
};
